import React from 'react';
import Paper from '@mui/material/Paper';
import InfoIcon from '@mui/icons-material/Info';
import { SummaryType, UserType } from '../../../common/commonTypes';
import MacrosChart from './MacrosChart';
import MacrosGoalsChart from './MacrosGoalsChart';
import CalDistrChart from './CalDistrChart'; //  old ring chart
import CalDistrChart2 from './CalDistrChart2';
import CalDistrGoalsChart2 from './CalDistrGoalsChart2';
import { IconButton, Popover } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import HillTopBanner from '../../HillTopBanner';
import { isUserUnsubscribed } from '../../../utils/helpers';

type SummaryComponentType = {
    weeklyAverages: SummaryType;
    dailySummary: SummaryType;
    goals: SummaryType;
    macrosChartType: 'GOALS' | 'BASELINE';
    user: UserType;
};

const keys: any = {
    intakeDescription: {
        GOALS: 'macros_intake_goals_chart_description',
        BASELINE: 'macros_intake_chart_description',
    },
    compositionDescription: {
        GOALS: 'macros_composition_goals_chart_description',
        BASELINE: 'macros_composition_chart_description',
    },
    macrosCharRightHeading: {
        GOALS: 'goal',
        BASELINE: 'weekly_baseline',
    },
};

const Summary = (props: SummaryComponentType) => {
    const { dailySummary, weeklyAverages, goals, macrosChartType, user } = props;
    const userIsUnsubscribed: boolean = isUserUnsubscribed(user);

    const { t, i18n } = useTranslation();

    const [intakeAnchorEl, setIntakeAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [compositionAnchorEl, setCompositionAnchorEl] = React.useState<HTMLButtonElement | null>(
        null
    );

    // for info popovers
    const handleIntakeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setIntakeAnchorEl(event.currentTarget);
    };
    const handleIntakeClose = () => {
        setIntakeAnchorEl(null);
    };
    const handleCompositionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setCompositionAnchorEl(event.currentTarget);
    };
    const handleCompositionClose = () => {
        setCompositionAnchorEl(null);
    };

    return (
        <section className="pb-[145px]">
            <Paper className="m-4 px-4 pt-2 pb-4" elevation={3}>
                <h3 className="text-center mb-2">
                    <Trans i18nKey="macros_intake_chart_title" />
                    <IconButton onClick={handleIntakeClick}>
                        <InfoIcon fontSize="small" color="secondary" />
                    </IconButton>
                    <Popover
                        open={Boolean(intakeAnchorEl)}
                        anchorEl={intakeAnchorEl}
                        onClose={handleIntakeClose}
                    >
                        <div className="p-4 text-sm">
                            <Trans i18nKey={keys.intakeDescription[macrosChartType] as string} />
                        </div>
                    </Popover>
                </h3>

                {/* --- CHART TABLE TITLES START ---- */}
                {macrosChartType === 'BASELINE' && (
                    <div className="text-sm flex justify-between mb-2">
                        <div>
                            <strong>{t('today')}</strong>
                        </div>
                        <div>
                            <strong>{t(keys.macrosCharRightHeading[macrosChartType])}</strong>
                        </div>
                    </div>
                )}

                {macrosChartType === 'GOALS' && (
                    <table className="text-sm w-full mb-2">
                        <tbody>
                            <tr>
                                <td className="w-20">
                                    <strong>{t('today')}</strong>
                                </td>
                                <td className={`w-10`}></td>
                                <td className="w-auto"></td>
                                <td className="w-10 text-right text-gray-400 pr-1 text-sm">
                                    <strong className="text-right  text-gray-400">
                                        {t('remaining')}
                                    </strong>
                                </td>
                                <td className="w-10 text-right">
                                    <strong>
                                        {t(keys.macrosCharRightHeading[macrosChartType])}
                                    </strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                )}
                {/* --- CHART TABLE TITLES END ---- */}

                {!dailySummary.calories && !weeklyAverages.calories ? (
                    <div className="mt-6 text-center text-gray-700">
                        <div className="text-sm">{t('no_data_in_section_yet')}</div>
                        <div className="text-xs mt-2">{t('tell_us_what_you_ate_today')}</div>
                    </div>
                ) : (
                    <>
                        {macrosChartType === 'BASELINE' && (
                            <MacrosChart
                                weeklyAverages={weeklyAverages}
                                dailySummary={dailySummary}
                            />
                        )}
                        {macrosChartType === 'GOALS' && (
                            <MacrosGoalsChart goals={goals} dailySummary={dailySummary} />
                        )}
                    </>
                )}
            </Paper>

            {userIsUnsubscribed && <HillTopBanner height="tall" />}

            <Paper className="m-4 px-4 pt-2 pb-4" elevation={3}>
                <div>
                    <h3 className="text-center mt-0">
                        {t('macros_composition_chart_title')}
                        <IconButton onClick={handleCompositionClick}>
                            <InfoIcon fontSize="small" color="secondary" />
                        </IconButton>
                        <Popover
                            open={Boolean(compositionAnchorEl)}
                            anchorEl={compositionAnchorEl}
                            onClose={handleCompositionClose}
                            // anchorOrigin={{
                            //     vertical: 'bottom',
                            //     horizontal: 'left',
                            // }}
                        >
                            <div className="p-4 text-sm">
                                <Trans
                                    i18nKey={keys.compositionDescription[macrosChartType] as string}
                                />
                            </div>
                        </Popover>
                    </h3>

                    {/* chart table titles */}
                    <div className="text-sm flex justify-between">
                        <div>
                            <strong>{t('today')}</strong>
                        </div>
                        <div>
                            <strong>{t(keys.macrosCharRightHeading[macrosChartType])}</strong>
                        </div>
                    </div>
                    {!dailySummary.calories && !weeklyAverages.calories ? (
                        <div className="mt-6 text-center text-gray-700">
                            <div className="text-sm">{t('no_data_in_section_yet')}</div>
                            <div className="text-xs mt-2">{t('tell_us_what_you_ate_today')}</div>
                        </div>
                    ) : (
                        <div style={{ margin: '-30px 0 -30px 0' }}>
                            {/*<CalDistrChart*/}
                            {/*    weeklyAverages={weeklyAverages}*/}
                            {/*    dailySummary={dailySummary}*/}
                            {/*/>*/}

                            {macrosChartType === 'BASELINE' && (
                                <CalDistrChart2
                                    weeklyAverages={weeklyAverages}
                                    dailySummary={dailySummary}
                                />
                            )}
                            {macrosChartType === 'GOALS' && (
                                <CalDistrGoalsChart2 goals={goals} dailySummary={dailySummary} />
                            )}
                        </div>
                    )}
                </div>
            </Paper>
        </section>
    );
};

export default Summary;
